import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { adminAssessmentAPI, courseAPI, adminAPI } from '../services/api';
import toast from 'react-hot-toast';
import { COURSE_DURATION_OPTIONS, formatCourseDurationLabel } from '../constants/courseDurations';
import { resumeFileNameFromUrl, triggerBlobDownload } from '../utils/downloadFile';

function isTransientCourseGenerateError(err) {
  if (!err) return false;
  if (axios.isAxiosError(err)) {
    if (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK') return true;
    if (err.request && !err.response) return true;
    const s = err.response?.status;
    if (s === 502 || s === 503 || s === 504) return true;
    const msg = String(err.message || '').toLowerCase();
    if (msg.includes('timeout') || msg.includes('network error')) return true;
  }
  return false;
}

function courseGenerationLooksComplete(course) {
  return Boolean(course?.id) && Array.isArray(course.days) && course.days.length > 0;
}

function pick(obj, camel, snake) {
  if (!obj) return undefined;
  if (obj[camel] !== undefined && obj[camel] !== null) return obj[camel];
  return obj[snake];
}

/** Normalize admin candidate-by-user-id API payload for the details modal. */
function mergeCandidateDetailForModal(data) {
  const acc = data?.account ?? {};
  const p = data?.profile ?? {};
  return {
    firstName: pick(acc, 'firstName', 'first_name') || '',
    lastName: pick(acc, 'lastName', 'last_name') || '',
    email: pick(acc, 'email', 'email') || '',
    phone: pick(acc, 'phone', 'phone'),
    isActive: pick(acc, 'isActive', 'is_active') ?? false,
    createdAt: pick(acc, 'createdAt', 'created_at') || '',
    userId: pick(p, 'userId', 'user_id'),
    address: pick(p, 'address', 'address'),
    title: pick(p, 'title', 'title'),
    experienceYears: pick(p, 'experienceYears', 'experience_years'),
    profileCompletion:
      pick(p, 'profileCompletionPercentage', 'profile_completion_percentage') ?? 0,
    skills: pick(p, 'skills', 'skills'),
    bio: pick(p, 'bio', 'bio'),
    summary: pick(p, 'summary', 'summary'),
    education: pick(p, 'education', 'education'),
    linkedinUrl: pick(p, 'linkedinUrl', 'linkedin_url'),
    resumeUrl: pick(p, 'resumeUrl', 'resume_url'),
  };
}

/** Normalize progress API row to per-course lines (handles snake_case). */
function normalizeProgressItems(row) {
  const raw = row?.items;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((it) => ({
      courseName: pick(it, 'courseName', 'course_name') || '',
      completionStatus: pick(it, 'completionStatus', 'completion_status') || 'InProgress',
      enrollmentId: pick(it, 'enrollmentId', 'enrollment_id') || '',
      courseId: pick(it, 'courseId', 'course_id') || '',
      hasSubmission: Boolean(pick(it, 'hasSubmission', 'has_submission')),
      submissionFileName: pick(it, 'submissionFileName', 'submission_file_name'),
    }));
  }
  return [];
}

function progressStatusBadgeClass(status) {
  if (status === 'Completed') {
    return 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-600/20';
  }
  return 'bg-amber-100 text-amber-900 ring-1 ring-amber-600/25';
}

function progressRowAccentClass(status) {
  if (status === 'Completed') {
    return 'border-l-emerald-500 bg-emerald-50/50';
  }
  return 'border-l-amber-500 bg-amber-50/50';
}

const AdminCourseManagement = ({
  generationJobs,
  updateGenerationJob,
  dismissGenerationJob,
  appendGenerationJob,
}) => {
  const [tab, setTab] = useState('create');
  const [categories, setCategories] = useState([]);
  const [skillsOverview, setSkillsOverview] = useState([]);
  const [skillsByCategory, setSkillsByCategory] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSkillsForCategory, setLoadingSkillsForCategory] = useState(false);
  const [useNewSkill, setUseNewSkill] = useState(false);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [pending, setPending] = useState([]);
  const [candidateDisplayNames, setCandidateDisplayNames] = useState({});
  const [loadingPending, setLoadingPending] = useState(false);
  const [progressRows, setProgressRows] = useState([]);
  const [progressNames, setProgressNames] = useState({});
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [adminSubmissionDownloadingId, setAdminSubmissionDownloadingId] = useState('');
  const [candidateModalOpen, setCandidateModalOpen] = useState(false);
  const [candidateModalLoading, setCandidateModalLoading] = useState(false);
  const [candidateModalRow, setCandidateModalRow] = useState(null);
  const [resumeDownloading, setResumeDownloading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const generateWatchIntervalRef = useRef(null);

  const clearGenerateWatch = useCallback(() => {
    if (generateWatchIntervalRef.current) {
      clearInterval(generateWatchIntervalRef.current);
      generateWatchIntervalRef.current = null;
    }
  }, []);

  useEffect(() => () => clearGenerateWatch(), [clearGenerateWatch]);

  const [form, setForm] = useState({
    category: '',
    skill_name: '',
    duration_days: 15,
    audience_note: '',
    extra_instructions: '',
    publish: true,
  });

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await adminAssessmentAPI.getSkillCategories();

      if (response.status === 200 && response.data?.categories) {
        const categoriesList = response.data.categories;
        const hasOthers = categoriesList.some((cat) => cat.name === 'Others');
        if (!hasOthers) {
          categoriesList.push({
            id: 'others-temp',
            name: 'Others',
            description: "Category for custom skills that don't fit into existing categories",
            color_code: null,
            icon_url: null,
          });
        }
        const sortedCategories = categoriesList.sort((a, b) => {
          if (a.name === 'Others') return 1;
          if (b.name === 'Others') return -1;
          return a.name.localeCompare(b.name);
        });
        setCategories(sortedCategories);
      } else {
        setCategories([
          {
            id: 'others-temp',
            name: 'Others',
            description: "Category for custom skills that don't fit into existing categories",
            color_code: null,
            icon_url: null,
          },
        ]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error(`Failed to load categories: ${error.message}`);
      setCategories([
        {
          id: 'others-temp',
          name: 'Others',
          description: "Category for custom skills that don't fit into existing categories",
          color_code: null,
          icon_url: null,
        },
      ]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleCategoryChange = async (categoryName) => {
    setForm((prev) => ({ ...prev, category: categoryName, skill_name: '' }));
    setSkillsByCategory([]);
    setUseNewSkill(false);

    if (!categoryName) {
      return;
    }

    if (categoryName === 'Others') {
      setLoadingSkillsForCategory(false);
      const othersSkills = skillsOverview
        .filter((skill) => skill.category === 'Others')
        .map((skill) => ({ id: skill.skill_id, name: skill.skill_name }));
      setSkillsByCategory(othersSkills);
      setUseNewSkill(true);
      return;
    }

    try {
      setLoadingSkillsForCategory(true);
      const response = await adminAssessmentAPI.getSkillsByCategory(categoryName);

      if (response.status === 200 && response.data?.skills) {
        setSkillsByCategory(response.data.skills);
      } else {
        setSkillsByCategory([]);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setSkillsByCategory([]);
      } else {
        console.error('Error fetching skills by category:', error);
        toast.error(`Failed to load skills for category: ${error.message}`);
        setSkillsByCategory([]);
      }
    } finally {
      setLoadingSkillsForCategory(false);
    }
  };

  useEffect(() => {
    const loadOverview = async () => {
      try {
        const res = await adminAssessmentAPI.getSkillsOverview();
        setSkillsOverview(Array.isArray(res.data?.skills) ? res.data.skills : []);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load skills overview');
      }
    };
    loadOverview();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (form.category === 'Others') {
      const othersSkills = skillsOverview
        .filter((skill) => skill.category === 'Others')
        .map((skill) => ({ id: skill.skill_id, name: skill.skill_name }));
      setSkillsByCategory(othersSkills);
    }
  }, [skillsOverview, form.category]);

  const normalizeCourseList = (data) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.courses)) return data.courses;
    return [];
  };

  const normalizePendingList = (data) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.enrollments)) return data.enrollments;
    return [];
  };

  const loadCourses = useCallback(async (options = {}) => {
    const { silent = false, signal } = options;
    try {
      setLoadingCourses(true);
      const axiosOpts =
        typeof AbortSignal !== 'undefined' && signal instanceof AbortSignal ? { signal } : {};
      const res = await courseAPI.listCourses(axiosOpts);
      setCourses(normalizeCourseList(res.data));
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') {
        return;
      }
      console.error(e);
      if (!silent) toast.error('Failed to load courses');
    } finally {
      setLoadingCourses(false);
    }
  }, []);

  const loadPending = useCallback(async (options = {}) => {
    const { silent = false, signal } = options;
    try {
      setLoadingPending(true);
      const axiosOpts =
        typeof AbortSignal !== 'undefined' && signal instanceof AbortSignal ? { signal } : {};
      const res = await courseAPI.pendingEnrollments(axiosOpts);
      const list = normalizePendingList(res.data);
      setPending(list);
      const ids = [...new Set(list.map((r) => String(r.user_id ?? '').trim()).filter(Boolean))];
      if (ids.length === 0) {
        setCandidateDisplayNames({});
      } else {
        try {
          const nameRes = await adminAPI.resolveCandidateDisplayNames(ids);
          setCandidateDisplayNames(nameRes.data?.names || {});
        } catch (nameErr) {
          if (nameErr?.code === 'ERR_CANCELED' || nameErr?.name === 'CanceledError') {
            return;
          }
          console.error(nameErr);
          setCandidateDisplayNames({});
        }
      }
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') {
        return;
      }
      console.error(e);
      if (!silent) toast.error('Failed to load pending requests');
      setCandidateDisplayNames({});
    } finally {
      setLoadingPending(false);
    }
  }, []);

  const loadProgress = useCallback(async (options = {}) => {
    const { silent = false, signal } = options;
    try {
      setLoadingProgress(true);
      const axiosOpts =
        typeof AbortSignal !== 'undefined' && signal instanceof AbortSignal ? { signal } : {};
      const res = await courseAPI.courseProgressOverview(axiosOpts);
      const list = Array.isArray(res.data) ? res.data : [];
      setProgressRows(list);
      const ids = [...new Set(list.map((r) => String(r.user_id ?? r.userId ?? '').trim()).filter(Boolean))];
      if (ids.length === 0) {
        setProgressNames({});
      } else {
        try {
          const nameRes = await adminAPI.resolveCandidateDisplayNames(ids);
          setProgressNames(nameRes.data?.names || {});
        } catch (nameErr) {
          if (nameErr?.code === 'ERR_CANCELED' || nameErr?.name === 'CanceledError') {
            return;
          }
          console.error(nameErr);
          setProgressNames({});
        }
      }
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') {
        return;
      }
      console.error(e);
      if (!silent) toast.error('Failed to load learner progress');
      setProgressRows([]);
      setProgressNames({});
    } finally {
      setLoadingProgress(false);
    }
  }, []);

  const startCourseReadyWatch = useCallback(
    (jobId, courseId, payload, { immediate = false } = {}) => {
      clearGenerateWatch();
      const norm = (s) => String(s ?? '').trim().toLowerCase();
      const skillKey = norm(payload.skill_name);
      const catKey = norm(payload.category);
      const durationDays = Number(payload.duration_days);

      const tick = async () => {
        try {
          if (courseId) {
            const detail = await courseAPI.getCourse(courseId);
            if (courseGenerationLooksComplete(detail.data)) {
              clearGenerateWatch();
              updateGenerationJob(jobId, {
                status: 'success',
                courseId: detail.data.id,
                message: 'Course is ready.',
              });
              toast.success('Course generated successfully.');
              await loadCourses({ silent: true });
              return;
            }
          }
          const lr = await courseAPI.listCourses();
          const list = normalizeCourseList(lr.data);
          setCourses(list);
          const match =
            (courseId && list.find((c) => c.id === courseId)) ||
            list.find(
              (c) =>
                norm(c.skill_name) === skillKey &&
                norm(c.category) === catKey &&
                Number(c.duration_days) === durationDays &&
                (!c.created_at ||
                  Date.now() - new Date(c.created_at).getTime() < 25 * 60 * 1000)
            );
          if (match?.id) {
            const detail = await courseAPI.getCourse(match.id);
            if (courseGenerationLooksComplete(detail.data)) {
              clearGenerateWatch();
              updateGenerationJob(jobId, {
                status: 'success',
                courseId: detail.data.id,
                message: 'Course is ready.',
              });
              toast.success('Course generated successfully.');
              return;
            }
          }
        } catch (_) {
          /* ignore polling errors */
        }
      };

      if (immediate) {
        void tick();
      }
      let ticks = 0;
      generateWatchIntervalRef.current = setInterval(() => {
        ticks += 1;
        if (ticks > 40) {
          clearGenerateWatch();
          updateGenerationJob(jobId, {
            status: 'error',
            error:
              'Generation is taking longer than expected. Check All courses or try again in a few minutes.',
          });
          return;
        }
        void tick();
      }, 10000);
    },
    [clearGenerateWatch, loadCourses, updateGenerationJob]
  );

  useEffect(() => {
    const controller = new AbortController();
    if (tab === 'list') {
      loadCourses({ silent: false, signal: controller.signal });
    }
    if (tab === 'pending') {
      loadPending({ silent: false, signal: controller.signal });
    }
    if (tab === 'progress') {
      loadProgress({ silent: false, signal: controller.signal });
    }
    return () => controller.abort();
  }, [tab, loadCourses, loadPending, loadProgress]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!form.category) {
      toast.error('Select a category');
      return;
    }
    if (!form.skill_name.trim()) {
      toast.error('Select or enter a skill name');
      return;
    }
    const norm = (s) => String(s ?? '').trim().toLowerCase();
    const catKey = norm(form.category);
    const skillKey = norm(form.skill_name);
    const durationDays = Number(form.duration_days);
    try {
      const listRes = await courseAPI.listCourses();
      const existing = normalizeCourseList(listRes.data);
      setCourses(existing);
      const isDup = existing.some(
        (c) =>
          norm(c.skill_name) === skillKey &&
          norm(c.category) === catKey &&
          Number(c.duration_days) === durationDays
      );
      if (isDup) {
        toast.error(
          `A course already exists for this category, skill, and duration (${formatCourseDurationLabel(durationDays)}).`
        );
        return;
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load courses to check for duplicates');
      return;
    }

    const payload = {
      category: form.category.trim(),
      skill_name: form.skill_name.trim(),
      duration_days: form.duration_days,
      audience_note: form.audience_note,
      extra_instructions: form.extra_instructions,
      publish: form.publish,
    };
    const label = [payload.category, payload.skill_name, formatCourseDurationLabel(payload.duration_days)].join(' · ');
    const jobId = `gen-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    clearGenerateWatch();

    appendGenerationJob({
      id: jobId,
      status: 'running',
      label,
      startedAt: Date.now(),
    });

    void (async () => {
      try {
        const res = await courseAPI.generateCourse(payload);
        const courseId = res.data?.course?.id;
        const accepted = res.status === 202 || res.data?.accepted === true;

        if (courseId && (accepted || !courseGenerationLooksComplete(res.data?.course))) {
          updateGenerationJob(jobId, {
            status: 'running',
            courseId,
            message: res.data?.message || 'Generation started…',
          });
          toast.success('Course generation started. You can stay on this page—we will notify you when it is ready.');
          setForm((prev) => ({ ...prev, skill_name: '' }));
          setUseNewSkill(false);
          startCourseReadyWatch(jobId, courseId, payload, { immediate: true });
          return;
        }

        clearGenerateWatch();
        updateGenerationJob(jobId, {
          status: 'success',
          courseId,
          message: res.data?.message || 'Course generated',
        });
        toast.success(res.data?.message || 'Course generated');
        setForm((prev) => ({ ...prev, skill_name: '' }));
        setUseNewSkill(false);
        await loadCourses({ silent: true });
      } catch (err) {
        console.error(err);
        const detail = err.response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : 'Failed to generate course';

        if (isTransientCourseGenerateError(err)) {
          updateGenerationJob(jobId, {
            status: 'running',
            message:
              'Connection to the server was interrupted, but generation may still be running. Checking for the new course…',
          });
          toast(
            'Connection interrupted while starting generation. We are checking whether the course still appears…',
            { icon: '⏳' }
          );
          startCourseReadyWatch(jobId, null, payload, { immediate: true });
        } else {
          updateGenerationJob(jobId, { status: 'error', error: msg });
          toast.error(msg);
        }
      }
    })();
  };

  const openCourseDetail = async (id) => {
    try {
      setLoadingDetail(true);
      const res = await courseAPI.getCourse(id);
      setSelectedCourse(res.data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load course');
    } finally {
      setLoadingDetail(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const openCandidateDetails = async (userId) => {
    const uid = String(userId ?? '').trim();
    if (!uid) {
      toast.error('Missing user id');
      return;
    }
    setCandidateModalOpen(true);
    setCandidateModalLoading(true);
    setCandidateModalRow(null);
    try {
      const res = await adminAPI.getCandidateByUserId(uid);
      setCandidateModalRow(mergeCandidateDetailForModal(res.data));
    } catch (e) {
      console.error(e);
      const detail = e.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : e.response?.status === 404
            ? 'No candidate profile for this user'
            : 'Failed to load candidate details'
      );
      setCandidateModalOpen(false);
    } finally {
      setCandidateModalLoading(false);
    }
  };

  const closeCandidateDetails = () => {
    setCandidateModalOpen(false);
    setCandidateModalRow(null);
    setCandidateModalLoading(false);
    setResumeDownloading(false);
  };

  const downloadCandidateResume = async () => {
    const row = candidateModalRow;
    const uid = String(row?.userId ?? '').trim();
    if (!uid || !row?.resumeUrl) {
      toast.error('No resume available');
      return;
    }
    try {
      setResumeDownloading(true);
      const res = await adminAPI.downloadCandidateResumeByUserId(uid);
      triggerBlobDownload(res.data, resumeFileNameFromUrl(row.resumeUrl));
      toast.success('Resume downloaded');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Could not download resume');
    } finally {
      setResumeDownloading(false);
    }
  };

  const approve = async (enrollmentId) => {
    try {
      await courseAPI.approveEnrollment(enrollmentId, {});
      toast.success('Approved');
      loadPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    }
  };

  const reject = async (enrollmentId) => {
    try {
      await courseAPI.rejectEnrollment(enrollmentId, {});
      toast.success('Rejected');
      loadPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    }
  };

  const downloadAdminSubmission = async (enrollmentId, suggestedFileName) => {
    const eid = String(enrollmentId || '').trim();
    if (!eid) return;
    try {
      setAdminSubmissionDownloadingId(eid);
      const res = await courseAPI.downloadAdminCourseSubmission(eid);
      const name = suggestedFileName || 'course-submission';
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Could not download submission');
    } finally {
      setAdminSubmissionDownloadingId('');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Courses</h3>
        <p className="text-sm text-gray-600">
          AI-generated day-wise courses by skill. No question bank — content is produced by OpenAI when you create a course.
        </p>
      </div>

      <nav className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
        {['create', 'list', 'pending', 'progress'].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => {
              setTab(t);
              if (t !== 'list') setSelectedCourse(null);
            }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${
              tab === t ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t === 'create' && (
              <>
                Create course
                {generationJobs.some((j) => j.status === 'running') && (
                  <span className="ml-1.5 inline-flex h-2 w-2 rounded-full bg-amber-300 animate-pulse" title="Generation in progress" />
                )}
              </>
            )}
            {t === 'list' && 'All courses'}
            {t === 'pending' && 'Access requests'}
            {t === 'progress' && 'Progress'}
          </button>
        ))}
      </nav>

      {generationJobs.some((j) => j.status === 'running') && tab !== 'create' && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          Course generation is running in the background. Open{' '}
          <button
            type="button"
            className="font-medium text-primary-700 underline hover:text-primary-800"
            onClick={() => setTab('create')}
          >
            Create course
          </button>{' '}
          to see progress.
        </div>
      )}

      {tab === 'create' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
          <form
            onSubmit={handleGenerate}
            className="space-y-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
          >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Category *</label>
              <select
                value={form.category}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
                disabled={loadingCategories}
              >
                <option value="">Select a category first</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
              {loadingCategories && (
                <p className="mt-1 text-xs text-gray-500">Loading categories…</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Skill Name *</label>
              {!form.category ? (
                <div className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-gray-500">
                  Please select a category first
                </div>
              ) : form.category === 'Others' ? (
                <>
                  <input
                    type="text"
                    value={form.skill_name}
                    onChange={(e) => setForm({ ...form, skill_name: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter skill name (e.g., Custom Skill)"
                    required
                  />
                  {skillsByCategory.length > 0 && (
                    <div className="mt-3">
                      <p className="mb-2 text-xs font-medium text-gray-700">
                        Existing skills in &quot;Others&quot; category:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {skillsByCategory.map((skill) => (
                          <span
                            key={skill.id}
                            className="rounded-md border border-gray-200 bg-gray-100 px-2 py-1 text-xs text-gray-700"
                          >
                            {skill.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : loadingSkillsForCategory ? (
                <div className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2">
                  <p className="text-sm text-gray-500">Loading skills…</p>
                </div>
              ) : !useNewSkill && skillsByCategory.length > 0 ? (
                <>
                  <select
                    value={form.skill_name}
                    onChange={(e) => {
                      if (e.target.value === '__new__') {
                        setUseNewSkill(true);
                        setForm({ ...form, skill_name: '' });
                      } else {
                        setForm({ ...form, skill_name: e.target.value });
                      }
                    }}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value="">Select a skill</option>
                    {skillsByCategory.map((skill) => (
                      <option key={skill.id} value={skill.name}>
                        {skill.name}
                      </option>
                    ))}
                    <option value="__new__">+ Add New Skill</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Select an existing skill or choose &quot;+ Add New Skill&quot; to type a new one
                  </p>
                </>
              ) : (
                <>
                  <input
                    type="text"
                    value={form.skill_name}
                    onChange={(e) => setForm({ ...form, skill_name: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter skill name (e.g., Python, JavaScript)"
                    required
                  />
                  {skillsByCategory.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setUseNewSkill(false);
                        setForm({ ...form, skill_name: '' });
                      }}
                      className="mt-2 text-xs text-primary-600 underline hover:text-primary-700"
                    >
                      ← Select from existing skills
                    </button>
                  )}
                  {skillsByCategory.length === 0 && (
                    <p className="mt-1 text-xs text-yellow-600">
                      No skills in this category yet. Enter a name that matches how it will appear on candidate
                      profiles.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Duration *</label>
            <select
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={form.duration_days}
              onChange={(e) =>
                setForm({ ...form, duration_days: parseInt(e.target.value, 10) })
              }
              required
            >
              {COURSE_DURATION_OPTIONS.map((opt) => (
                <option key={opt.days} value={opt.days}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Audience (optional)</label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              rows={2}
              value={form.audience_note}
              onChange={(e) => setForm({ ...form, audience_note: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Extra instructions (optional)</label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              rows={3}
              value={form.extra_instructions}
              onChange={(e) => setForm({ ...form, extra_instructions: e.target.value })}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.publish}
              onChange={(e) => setForm({ ...form, publish: e.target.checked })}
            />
            Publish immediately (candidates can see it when it matches their skills)
          </label>
          <button
            type="submit"
            className="w-full py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Generate course (runs in background)
          </button>
        </form>

          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="border-b border-gray-100 pb-3">
              <h4 className="text-base font-semibold text-gray-900">Generation activity</h4>
              <p className="mt-1 text-xs text-gray-600">
                Status for jobs started from this page. While the course service is busy, the course list may load more
                slowly.
              </p>
            </div>
            <div className="mt-4">
            {generationJobs.length === 0 ? (
              <p className="rounded-md border border-dashed border-gray-200 bg-white px-3 py-6 text-center text-sm text-gray-500">
                No active or recent jobs. Submit the form to start a generation.
              </p>
            ) : (
              <ul className="max-h-[min(70vh,520px)] space-y-2 overflow-y-auto pr-1">
                {generationJobs.map((job) => (
                  <li
                    key={job.id}
                    className="rounded-md border border-gray-200 bg-white p-3 text-sm shadow-sm"
                  >
                    <div className="font-medium text-gray-900">{job.label}</div>
                    {job.status === 'running' && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                        <span className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
                        {job.message || 'Generating… this can take several minutes.'}
                      </div>
                    )}
                    {job.status === 'success' && (
                      <div className="mt-2 space-y-2 text-xs text-green-800">
                        <p>{job.message || 'Done.'}</p>
                        <div className="flex flex-wrap gap-2">
                          {job.courseId && (
                            <button
                              type="button"
                              className="rounded bg-primary-600 px-2 py-1 text-white hover:bg-primary-700"
                              onClick={() => {
                                setTab('list');
                                openCourseDetail(job.courseId);
                              }}
                            >
                              View in list
                            </button>
                          )}
                          <button
                            type="button"
                            className="rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-50"
                            onClick={() => dismissGenerationJob(job.id)}
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    )}
                    {job.status === 'error' && (
                      <div className="mt-2 space-y-2 text-xs text-red-700">
                        <p>{job.error || 'Failed.'}</p>
                        <button
                          type="button"
                          className="rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-50"
                          onClick={() => dismissGenerationJob(job.id)}
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
            </div>
          </div>
        </div>
      )}

      {tab === 'list' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
              <h4 className="font-medium text-gray-900">Courses</h4>
              <button type="button" onClick={() => loadCourses()} className="text-sm text-primary-600">
                Refresh
              </button>
            </div>
            {loadingCourses ? (
              <p className="p-4 text-sm text-gray-500">Loading…</p>
            ) : (
              <ul className="divide-y divide-gray-100 max-h-[480px] overflow-y-auto">
                {courses.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => openCourseDetail(c.id)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${
                        selectedCourse?.id === c.id ? 'bg-primary-50' : ''
                      }`}
                    >
                      <div className="font-medium text-gray-900">{c.title}</div>
                      <div className="text-xs text-gray-500">
                        {[c.category, c.skill_name].filter(Boolean).join(' · ')} ·{' '}
                        {formatCourseDurationLabel(c.duration_days)} · {c.status}
                      </div>
                    </button>
                  </li>
                ))}
                {courses.length === 0 && (
                  <li className="px-4 py-6 text-sm text-gray-500">No courses yet.</li>
                )}
              </ul>
            )}
          </div>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 min-h-[200px]">
            <h4 className="font-medium text-gray-900 mb-2">Preview</h4>
            {loadingDetail && <p className="text-sm text-gray-500">Loading…</p>}
            {!loadingDetail && selectedCourse && (
              <div className="space-y-3 text-sm">
                <p className="text-xs text-gray-500">
                  {[selectedCourse.category, selectedCourse.skill_name].filter(Boolean).join(' · ')}
                </p>
                <p className="text-gray-700">{selectedCourse.summary}</p>
                <div className="space-y-4 max-h-[420px] overflow-y-auto">
                  {(selectedCourse.days || []).map((d) => (
                    <div key={d.id || d.day_number} className="border border-gray-100 rounded p-3">
                      <div className="font-semibold text-gray-900">
                        Day {d.day_number}: {d.title}
                      </div>
                      <pre className="mt-2 whitespace-pre-wrap text-gray-700 text-xs font-sans">
                        {d.content_md}
                      </pre>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!loadingDetail && !selectedCourse && (
              <p className="text-sm text-gray-500">Select a course to preview days.</p>
            )}
          </div>
        </div>
      )}

      {tab === 'pending' && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
            <h4 className="font-medium text-gray-900">Pending access requests</h4>
            <button type="button" onClick={() => loadPending()} className="text-sm text-primary-600">
              Refresh
            </button>
          </div>
          {loadingPending ? (
            <p className="p-4 text-sm text-gray-500">Loading…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-gray-600">
                    <th className="px-4 py-2">Course</th>
                    <th className="px-4 py-2">Skill</th>
                    <th className="px-4 py-2">Candidate</th>
                    <th className="px-4 py-2">Requested</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((row) => (
                    <tr key={row.id} className="border-t border-gray-100">
                      <td className="px-4 py-2">{row.course_title}</td>
                      <td className="px-4 py-2">{row.skill_name}</td>
                      <td className="px-4 py-2">
                        <div className="font-medium text-gray-900">
                          {candidateDisplayNames[row.user_id] || (
                            <span className="text-gray-500" title={row.user_id}>
                              Unknown candidate
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        {row.requested_at ? new Date(row.requested_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="inline-flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
                            onClick={() => openCandidateDetails(row.user_id)}
                          >
                            View details
                          </button>
                          <button
                            type="button"
                            className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                            onClick={() => approve(row.id)}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                            onClick={() => reject(row.id)}
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {pending.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                        No pending requests.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'progress' && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
            <div>
              <h4 className="font-medium text-gray-900">Learner course progress</h4>
              <p className="mt-0.5 text-xs text-gray-500">
                Each course is shown on its own line with a color-coded status. Rejected enrollments are omitted.
                Completed means the learner finished and uploaded a final submission; use the Download button to retrieve that file.
              </p>
            </div>
            <button type="button" onClick={() => loadProgress()} className="shrink-0 text-sm text-primary-600">
              Refresh
            </button>
          </div>
          {loadingProgress ? (
            <p className="p-4 text-sm text-gray-500">Loading…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-gray-600">
                    <th className="px-4 py-2">Candidate</th>
                    <th className="px-4 py-2">Courses & status</th>
                  </tr>
                </thead>
                <tbody>
                  {[...progressRows]
                    .sort((a, b) => {
                      const ida = String(a.user_id ?? a.userId ?? '');
                      const idb = String(b.user_id ?? b.userId ?? '');
                      const na = progressNames[ida] || ida;
                      const nb = progressNames[idb] || idb;
                      return na.localeCompare(nb, undefined, { sensitivity: 'base' });
                    })
                    .map((row) => {
                      const uid = String(row.user_id ?? row.userId ?? '');
                      const items = normalizeProgressItems(row);
                      return (
                        <tr key={uid || 'row'} className="border-t border-gray-100">
                          <td className="px-3 py-2 align-top text-sm">
                            <div className="font-medium text-gray-900">
                              {progressNames[uid] || (
                                <span className="text-gray-500" title={uid}>
                                  Unknown candidate
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="min-w-[200px] max-w-md px-3 py-2 align-top">
                            {items.length === 0 ? (
                              <span className="text-xs text-gray-400">—</span>
                            ) : (
                              <div className="max-h-32 overflow-y-auto pr-0.5">
                                <ul className="flex flex-col gap-1">
                                  {items.map((it, idx) => (
                                    <li
                                      key={`${uid}-${it.enrollmentId || idx}-${it.courseName}`}
                                      className={`flex flex-wrap items-center justify-between gap-1.5 rounded-sm border border-gray-200 border-l-2 py-1 pl-2 pr-1.5 ${progressRowAccentClass(
                                        it.completionStatus
                                      )}`}
                                    >
                                      <span className="min-w-0 flex-1 text-xs font-medium leading-tight text-gray-900">
                                        {it.courseName}
                                      </span>
                                      <div className="flex shrink-0 flex-wrap items-center gap-1">
                                        <span
                                          className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none ${progressStatusBadgeClass(
                                            it.completionStatus
                                          )}`}
                                        >
                                          {it.completionStatus === 'Completed' ? 'Completed' : 'In progress'}
                                        </span>
                                        {it.completionStatus === 'Completed' &&
                                          it.hasSubmission &&
                                          it.enrollmentId && (
                                            <button
                                              type="button"
                                              disabled={adminSubmissionDownloadingId === it.enrollmentId}
                                              onClick={() =>
                                                downloadAdminSubmission(
                                                  it.enrollmentId,
                                                  it.submissionFileName ||
                                                    `${it.courseName.replace(/[/\\?%*:|"<>]/g, '-')}-submission`
                                                )
                                              }
                                              className="inline-flex items-center justify-center rounded-md bg-primary-600 px-2.5 py-1 text-xs font-semibold text-white shadow-sm ring-1 ring-primary-600/20 hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:bg-primary-400 disabled:hover:bg-primary-400"
                                            >
                                              {adminSubmissionDownloadingId === it.enrollmentId
                                                ? 'Downloading…'
                                                : 'Download'}
                                            </button>
                                          )}
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  {progressRows.length === 0 && (
                    <tr>
                      <td colSpan={2} className="px-4 py-6 text-center text-gray-500">
                        No enrollment data yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {candidateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white shadow-xl">
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
              <h3 className="text-xl font-bold text-gray-900">Candidate details</h3>
              <button
                type="button"
                onClick={closeCandidateDetails}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              {candidateModalLoading && <p className="text-sm text-gray-500">Loading…</p>}
              {!candidateModalLoading && candidateModalRow && (
                <>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="space-y-4">
                      <h4 className="border-b border-gray-200 pb-2 text-lg font-semibold text-gray-900">
                        Personal information
                      </h4>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Full name</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {candidateModalRow.firstName} {candidateModalRow.lastName}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Email</label>
                        <p className="mt-1 text-sm text-gray-900">{candidateModalRow.email || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Phone</label>
                        <p className="mt-1 text-sm text-gray-900">{candidateModalRow.phone || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Address</label>
                        <p className="mt-1 text-sm text-gray-900">{candidateModalRow.address || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Status</label>
                        <div className="mt-1">
                          <span
                            className={`rounded-full px-2 py-1 text-xs ${
                              candidateModalRow.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {candidateModalRow.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Registered</label>
                        <p className="mt-1 text-sm text-gray-900">{formatDate(candidateModalRow.createdAt)}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <h4 className="border-b border-gray-200 pb-2 text-lg font-semibold text-gray-900">
                        Professional information
                      </h4>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Title</label>
                        <p className="mt-1 text-sm text-gray-900">{candidateModalRow.title || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Experience</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {candidateModalRow.experienceYears != null && candidateModalRow.experienceYears !== ''
                            ? `${candidateModalRow.experienceYears} years`
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Profile completion</label>
                        <div className="mt-1 flex items-center gap-2">
                          <div className="h-2 flex-1 rounded-full bg-gray-200">
                            <div
                              className="h-2 rounded-full bg-primary-600"
                              style={{ width: `${candidateModalRow.profileCompletion || 0}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600">
                            {candidateModalRow.profileCompletion || 0}%
                          </span>
                        </div>
                      </div>
                      {candidateModalRow.skills && candidateModalRow.skills.length > 0 && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Skills</label>
                          <div className="mt-1 flex flex-wrap gap-2">
                            {candidateModalRow.skills.map((skill, idx) => (
                              <span key={idx} className="rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {candidateModalRow.bio && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Bio</label>
                          <p className="mt-1 text-sm text-gray-900">{candidateModalRow.bio}</p>
                        </div>
                      )}
                      {candidateModalRow.summary && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Summary</label>
                          <p className="mt-1 text-sm text-gray-900">{candidateModalRow.summary}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  {(candidateModalRow.education || candidateModalRow.linkedinUrl || candidateModalRow.resumeUrl) && (
                    <div className="mt-6 border-t border-gray-200 pt-6">
                      <h4 className="mb-4 text-lg font-semibold text-gray-900">Additional information</h4>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {candidateModalRow.education && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">Education</label>
                            <p className="mt-1 whitespace-pre-wrap text-sm text-gray-900">{candidateModalRow.education}</p>
                          </div>
                        )}
                        {candidateModalRow.linkedinUrl && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">LinkedIn</label>
                            <p className="mt-1 text-sm text-gray-900">
                              <a
                                href={candidateModalRow.linkedinUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                {candidateModalRow.linkedinUrl}
                              </a>
                            </p>
                          </div>
                        )}
                        {candidateModalRow.resumeUrl && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">Resume</label>
                            <p className="mt-1 text-sm text-gray-900">
                              <button
                                type="button"
                                onClick={() => void downloadCandidateResume()}
                                disabled={resumeDownloading}
                                className="text-blue-600 hover:underline disabled:opacity-50"
                              >
                                {resumeDownloading ? 'Downloading…' : 'Download resume'}
                              </button>
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="mt-6 flex justify-end border-t border-gray-200 pt-6">
                    <button
                      type="button"
                      onClick={closeCandidateDetails}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCourseManagement;
